import { useEffect, useState } from 'react'
import { Spin, Tag, Empty } from 'antd'
import { RobotOutlined } from '@ant-design/icons'
import { getPortfolioConfigSnapshot } from '@/api/portfolio'
import type { PortfolioConfigSnapshot } from '@/types/portfolio'
import type { StrategyParam } from '@/types'

const positionSizingLabel: Record<string, string> = {
    equal_weight: '等权分配',
    market_cap:   '市值加权',
    risk_parity:  '风险平价',
}

const benchmarkLabel: Record<string, string> = {
    HSI:     '恒生指数',
    HSCEI:   '恒生中企',
    HSTECH:  '恒生科技',
    CSI300:  '沪深 300',
    CSI500:  '中证 500',
    MSCI_CN: 'MSCI 中国',
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="cfg-snap__row">
            <span className="cfg-snap__row-label">{label}</span>
            <span className="cfg-snap__row-value">{value}</span>
        </div>
    )
}

function KV({ label, value, accent }: { label: string; value: React.ReactNode; accent?: boolean }) {
    return (
        <div className="cfg-snap__kv">
            <div className="cfg-snap__kv-label">{label}</div>
            <div className={`cfg-snap__kv-value${accent ? ' is-accent' : ''}`}>{value}</div>
        </div>
    )
}

function Section({ title, children, icon }: { title: React.ReactNode; children: React.ReactNode; icon?: React.ReactNode }) {
    return (
        <div className="cfg-snap__section">
            <div className="cfg-snap__section-title">{icon}{title}</div>
            {children}
        </div>
    )
}

function formatStrategyParam(p: StrategyParam): string {
    const unit = p.unit ? p.unit : ''
    if (p.type === 'range') {
        const lo = p.min ?? '-'
        const hi = p.max ?? '-'
        return `${lo} ~ ${hi}${unit ? ' ' + unit : ''}`
    }
    if (p.type === 'min') return `≥ ${p.min ?? '-'}${unit ? ' ' + unit : ''}`
    if (p.type === 'max') return `≤ ${p.max ?? '-'}${unit ? ' ' + unit : ''}`
    return '-'
}

export default function ConfigSnapshot({ portfolioId }: { portfolioId: number }) {
    const [data, setData] = useState<PortfolioConfigSnapshot | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')

    useEffect(() => {
        setLoading(true)
        getPortfolioConfigSnapshot(portfolioId)
            .then(setData)
            .catch(e => setError(e instanceof Error ? e.message : '加载失败'))
            .finally(() => setLoading(false))
    }, [portfolioId])

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
                <Spin />
            </div>
        )
    }

    if (error || !data) {
        return <Empty style={{ marginTop: 64 }} description={error || '暂无配置信息'} />
    }

    const rp = data.risk_params ?? {}
    const isLive = data.type === 'live'
    const market = data.strategy_version?.market ?? ''
    const benchmark = rp.benchmark as string | undefined
    const startDate = rp.start_date as string | undefined
    const maxPositions = (rp.max_positions as number) ?? 10
    const maxSingleWeight = (rp.max_single_weight as number) ?? 15
    const sectorLimit = (rp.sector_limit as number) ?? 40
    const maxDrawdown = (rp.max_drawdown as number) ?? 20
    const cashReserve = (rp.cash_reserve as number) ?? 5
    const positionSizing = (rp.position_sizing as string) ?? 'equal_weight'
    const useMonitor = Boolean(rp.use_monitor)
    const useResearch = Boolean(rp.use_research)
    const reinvestDividend = Boolean(rp.reinvest_dividend)
    const strategyParams = (Array.isArray(rp.strategy_params) ? rp.strategy_params : []) as StrategyParam[]

    return (
        <div className="cfg-snap">

            {/* 组合基本信息 */}
            <Section title="组合基本信息">
                <div className="cfg-snap__name">{data.name || '--'}</div>
                <div className="cfg-snap__tags-row">
                    <Tag className={`is-round text-11 ${isLive ? 'is-warning' : 'is-success'}`}>
                        {isLive ? '实盘' : '模拟盘'}
                    </Tag>
                    {market && (
                        <Tag className={`is-round text-11 ${market === 'HK' ? 'is-purple' : 'is-success'}`}>
                            {market}
                        </Tag>
                    )}
                    {benchmark && (
                        <Tag className="is-round text-11 border-tag">
                            基准：{benchmarkLabel[benchmark] ?? benchmark}
                        </Tag>
                    )}
                </div>
                {startDate && <Row label="建仓开始时间" value={startDate} />}
                <Row label="初始资金" value={<strong>{(data.initial_cash / 10000).toFixed(2)} 万元</strong>} />
                <Row label="股票池" value={
                    data.stock_pool
                        ? <Tag color="blue" style={{ borderColor: 'var(--accent)' }}>
                            {data.stock_pool.name}（{data.stock_pool.count} 只）
                          </Tag>
                        : <span style={{ color: 'var(--text-3)' }}>未设置</span>
                } />
                {data.description && <Row label="组合描述" value={data.description} />}
            </Section>

            {/* 策略配置 */}
            <Section title="策略配置">
                {data.manager && (
                    <Row label="组合经理" value={
                        <span>
                            <strong>{data.manager.nickname}</strong>
                            <span style={{ fontSize: 11, color: 'var(--text-3)', marginLeft: 8 }}>
                                {data.manager.style} · {data.manager.risk_level}
                            </span>
                        </span>
                    } />
                )}
                {data.strategy_version && (
                    <>
                        <Row label="激活版本" value={
                            <span>
                                <span style={{ color: 'var(--accent)', fontWeight: 700, marginRight: 6 }}>
                                    {data.strategy_version.version_no}
                                </span>
                                {data.strategy_version.name}
                            </span>
                        } />
                        {data.strategy_version.investment_brief && (
                            <Row label="投资摘要" value={
                                <span style={{ color: 'var(--text-2)', fontSize: 12 }}>
                                    {data.strategy_version.investment_brief}
                                </span>
                            } />
                        )}
                        {data.strategy_version.focus_sectors?.length > 0 && (
                            <Row label="关注板块" value={
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                    {data.strategy_version.focus_sectors.map(s => (
                                        <Tag key={s} className="border-tag text-11 is-round-tag">{s}</Tag>
                                    ))}
                                </div>
                            } />
                        )}
                        {data.strategy_version.constraints?.length > 0 && (
                            <Row label="策略约束" value={
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                    {data.strategy_version.constraints.map(c => (
                                        <Tag key={c} className="border-tag text-11 is-round-tag">{c}</Tag>
                                    ))}
                                </div>
                            } />
                        )}
                    </>
                )}
            </Section>

            {/* 风控约束 */}
            <Section title="风控约束">
                <div className="cfg-snap__grid cfg-snap__grid--4">
                    <KV label="单股仓位上限" value={`${maxSingleWeight}%`} />
                    <KV label="行业仓位上限" value={`${sectorLimit}%`} />
                    <KV label="最大回撤止损" value={`${maxDrawdown}%`} accent />
                    <KV label="最低现金留存" value={`${cashReserve}%`} />
                </div>
            </Section>

            {/* AI 授权范围 + 外部信号响应 */}
            <div className="cfg-snap__row-sections">
                <Section title="AI 授权范围" icon={<RobotOutlined style={{ marginRight: 6 }} />}>
                    <div className="cfg-snap__grid cfg-snap__grid--2">
                        <KV label="最大持仓数量" value={`${maxPositions} 只`} />
                        <KV label="仓位分配方式" value={positionSizingLabel[positionSizing] ?? positionSizing} />
                    </div>
                </Section>

                <Section title="外部信号响应">
                    {[
                        { label: '智能监控信号',    on: useMonitor },
                        { label: '投研 Agent 信号', on: useResearch },
                        { label: '分红再投资',       on: reinvestDividend },
                    ].map(({ label, on }) => (
                        <Row key={label} label={label} value={
                            <Tag className={`is-round text-11 ${on ? 'is-success' : ''}`}
                                style={{ opacity: on ? 1 : 0.45 }}>
                                {on ? '已启用' : '未启用'}
                            </Tag>
                        } />
                    ))}
                </Section>
            </div>

            {/* 策略 Skill 参数 */}
            <Section title="策略 Skill 参数" icon={<RobotOutlined style={{ marginRight: 6 }} />}>
                {strategyParams.length > 0 ? (
                    <div className="cfg-snap__grid cfg-snap__grid--4">
                        {strategyParams.map(p => (
                            <KV key={p.key} label={p.name} value={formatStrategyParam(p)} />
                        ))}
                    </div>
                ) : (
                    <Row label="策略 Skill 参数" value={
                        <span style={{ color: 'var(--text-3)' }}>未配置</span>
                    } />
                )}
            </Section>

        </div>
    )
}
